import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { IndicatorModal } from 'limetray-ui-kit';
import { useToast } from '../../../context/ToastContext';
import ManagerModal from '../../../components/ManagerModal';
import RoleBasicDetailsSection from './sections/RoleBasicDetailsSection';
import RoleBrandAccessSection from './sections/RoleBrandAccessSection';
import RolePermissionsSection from './sections/RolePermissionsSection';
import { userManagerConstants } from '../../../constants/userManagerConstants';
import { useFormValidation } from '../../../hooks/useFormValidation';
import { required } from '../../../utils/validationRules';
import ApiConstants from '../../../constants/apiConstants';
import http from '../../../utils/http';

const { createRoleModal } = userManagerConstants;
const {
  errorMessages,
  successMessages,
  modalHeader,
  navConfig,
  defaultSlug,
  hashPrefix,
  slug: SLUG,
  unsavedChangesConfirm,
} = createRoleModal;

/**
 * Initial empty state for the create/edit role form.
 */
const INITIAL_FORM = {
  roleGroupName: '',
  roleGroupDescription: '',
  brandId: '',
  roleIds: [],
  status: 'ACTIVE',
};

/**
 * CreateEditRoleModal
 *
 * Handles role creation/edit layout using ManagerModal.
 */
function CreateEditRoleModal({
  open,
  onClose,
  mode = 'create',
  // Create
  createRole,
  createRoleLoading = false,
  createRoleFeedback,
  clearCreateRoleFeedback,
  // Edit
  editRoleRow,
  editRole,
  editRoleLoading = false,
  editRoleFeedback,
  clearEditRoleFeedback,
  getRole,
  getRoleLoading = false,
  getRoleData,
  // Pagination / Search
  offset = 0,
  pageSize = 10,
  searchQuery = '',
}) {
  const isCreate = mode === 'create';
  const isEdit = mode === 'edit';

  const [brands, setBrands] = useState([]);
  const [showAdminButton, setShowAdminButton] = useState(false);
  const [permissionsHierarchy, setPermissionsHierarchy] = useState([]);
  const [flatPermissions, setFlatPermissions] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  
  const { showToast } = useToast();

  const initialFormRef = useRef(null);

  const fetchMetadata = useCallback(async () => {
    setDataLoading(true);
    try {
      const res = await http.get(ApiConstants.PERMISSIONS.GET_ALL.URL(), {});
      
      // 1. Map brands
      const fetchedBrands = res?.brands || {};
      const mappedBrands = Object.values(fetchedBrands).map((brand) => ({
        brandId: String(brand.brandId),
        brandName: brand.brandName,
      }));
      setBrands(mappedBrands);

      // 2. Check admin validity
      const userPermissions = res?.permissions || {};
      let isAdmin = false;
      if (userPermissions["user_manager"]?.features?.role_manager_dashboard?.accesses) {
        const accesses = userPermissions["user_manager"].features.role_manager_dashboard.accesses;
        isAdmin = accesses.some(a => a.roleIdentifier === "manage_roles_admin");
      }
      setShowAdminButton(isAdmin);

      // 3. Map permissions hierarchy & flat permissions
      const unmappedPermissions = res?.unmappedPermissions || [];
      const flatList = [];
      const productMap = {};

      unmappedPermissions.forEach((perm) => {
        const prodId = perm.productIdentifier;
        const prodName = perm.productName || perm.productIdentifier;
        const featId = perm.featureIdentifier;
        const featName = perm.featureName || perm.featureIdentifier;

        if (perm.accesses) {
          perm.accesses.forEach((access) => {
            flatList.push({
              roleId: String(access.roleId),
              roleName: access.roleName,
              featureName: featName,
              productName: prodName,
            });
          });
        }

        if (!productMap[prodId]) {
          productMap[prodId] = {
            id: prodId,
            name: prodName,
            features: [],
          };
        }

        const featureObj = {
          id: featId,
          name: featName,
          accesses: (perm.accesses || []).map((access) => ({
            roleId: String(access.roleId),
            roleName: access.roleName,
          })),
        };

        productMap[prodId].features.push(featureObj);
      });

      setFlatPermissions(flatList);
      setPermissionsHierarchy(Object.values(productMap));

    } catch (error) {
      console.error("Failed to fetch permissions metadata", error);
    } finally {
      setDataLoading(false);
    }
  }, []);

  // ── Validation rules ────────────────────────────────────────────────────────
  const validationRules = useMemo(() => ({
    roleGroupName: [required(errorMessages.requiredField)],
    roleGroupDescription: [required(errorMessages.requiredField)],
    brandId: [required(errorMessages.requiredField)],
  }), []);

  const {
    formData,
    errors,
    handleChange,
    handleBlur,
    validateForm,
    resetForm,
    setFormData,
  } = useFormValidation(INITIAL_FORM, validationRules);

  const lastFetchedIdRef = useRef(null);
  const wasOpenRef = useRef(false);

  // ── Reset / pre-populate on open ────────────────────────────────────────────
  useEffect(() => {
    if (!open) {
      wasOpenRef.current = false;
      lastFetchedIdRef.current = null;
      return;
    }

    // Only run if the modal was previously closed, or if the roleGroupId has changed
    const currentId = editRoleRow?.roleGroupId;
    if (wasOpenRef.current && lastFetchedIdRef.current === currentId) {
      return;
    }

    wasOpenRef.current = true;
    lastFetchedIdRef.current = currentId;

    resetForm();
    setBrands([]);
    setPermissionsHierarchy([]);
    setFlatPermissions([]);
    setShowAdminButton(false);
    
    fetchMetadata();

    if (isCreate) {
      clearCreateRoleFeedback?.();
      initialFormRef.current = { ...INITIAL_FORM };
    } else if (isEdit) {
      clearEditRoleFeedback?.();
      initialFormRef.current = null;
      // Fetch details of the selected role
      if (currentId) {
        getRole?.({ roleGroupId: currentId });
      }
    }
  }, [
    open,
    isCreate,
    isEdit,
    editRoleRow?.roleGroupId,
    resetForm,
    clearCreateRoleFeedback,
    clearEditRoleFeedback,
    getRole,
    fetchMetadata,
  ]);

  // Pre-populate form when getRoleData resolves (edit mode)
  useEffect(() => {
    if (isEdit && getRoleData && open) {
      const initial = {
        roleGroupName: getRoleData.roleGroupName || '',
        roleGroupDescription: getRoleData.roleGroupDescription || '',
        brandId: getRoleData.brandId ? String(getRoleData.brandId) : '',
        roleIds: Array.isArray(getRoleData.roleIds) ? getRoleData.roleIds.map(String) : [],
        status: getRoleData.status || 'ACTIVE',
      };
      setFormData(initial);
      initialFormRef.current = initial;
    }
  }, [getRoleData, isEdit, open, setFormData]);

  const hasUnsavedChanges = useCallback(() => {
    if (!initialFormRef.current) return false;
    
    const current = formData;
    const initial = initialFormRef.current;

    if ((current.roleGroupName || '') !== (initial.roleGroupName || '')) return true;
    if ((current.roleGroupDescription || '') !== (initial.roleGroupDescription || '')) return true;
    if (String(current.brandId || '') !== String(initial.brandId || '')) return true;
    if ((current.status || 'ACTIVE') !== (initial.status || 'ACTIVE')) return true;

    const currentRoleIds = current.roleIds || [];
    const initialRoleIds = initial.roleIds || [];
    if (currentRoleIds.length !== initialRoleIds.length) return true;

    const currentSorted = [...currentRoleIds].map(String).sort();
    const initialSorted = [...initialRoleIds].map(String).sort();
    for (let i = 0; i < currentSorted.length; i++) {
      if (currentSorted[i] !== initialSorted[i]) return true;
    }

    return false;
  }, [formData]);

  const handleClose = useCallback(() => {
    if (hasUnsavedChanges()) {
      setShowConfirmClose(true);
    } else {
      onClose();
    }
  }, [hasUnsavedChanges, onClose]);

  // ── Handle createRoleFeedback ───────────────────────────────────────────────
  useEffect(() => {
    if (!createRoleFeedback || !open) return;

    if (createRoleFeedback.type === 'success') {
      clearCreateRoleFeedback?.();
      showToast({
        variant: 'success',
        title: successMessages.createTitle,
        description: successMessages.createDesc,
      });
      onClose();
    } else if (createRoleFeedback.type === 'error') {
      clearCreateRoleFeedback?.();
      showToast({
        variant: 'error',
        title: errorMessages.createTitle,
        description: createRoleFeedback.message || errorMessages.createDesc,
      });
    }
  }, [createRoleFeedback, open, clearCreateRoleFeedback, onClose, showToast]);

  // ── Handle editRoleFeedback ─────────────────────────────────────────────────
  useEffect(() => {
    if (!editRoleFeedback || !open) return;

    if (editRoleFeedback.type === 'success') {
      clearEditRoleFeedback?.();
      showToast({
        variant: 'success',
        title: successMessages.editTitle,
        description: successMessages.editDesc,
      });
      onClose();
    } else if (editRoleFeedback.type === 'error') {
      clearEditRoleFeedback?.();
      showToast({
        variant: 'error',
        title: errorMessages.editTitle,
        description: editRoleFeedback.message || errorMessages.editDesc,
      });
    }
  }, [editRoleFeedback, open, clearEditRoleFeedback, onClose, showToast]);

  // ── Save handler ─────────────────────────────────────────────────────────────
  const handleSave = () => {
    if (isLoading) return;
    if (!validateForm()) return;

    const initialIds = getRoleData?.roleIds || [];
    const selectedIds = formData.roleIds || [];
    const removedRoleIds = initialIds
      .map(String)
      .filter(id => !selectedIds.map(String).includes(id));

    const payload = {
      roleGroupName: formData.roleGroupName,
      roleGroupDescription: formData.roleGroupDescription,
      brandId: formData.brandId,
      roleIds: selectedIds.map(Number),
      status: formData.status || 'ACTIVE',
      removedRoleIds: removedRoleIds.map(Number),
    };

    const paginationArgs = { offset, size: pageSize, searchTerm: searchQuery };

    if (isCreate) {
      createRole?.({
        payload,
        ...paginationArgs,
      });
    } else if (isEdit) {
      const roleGroupId = getRoleData?.roleGroupId || editRoleRow?.roleGroupId;
      editRole?.({
        roleGroupId,
        payload: {
          roleGroupId,
          ...payload,
        },
        ...paginationArgs,
      });
    }
  };

  const isLoading = isCreate
    ? createRoleLoading
    : getRoleLoading || editRoleLoading || dataLoading;

  const loadingMessage = isCreate
    ? createRoleModal.loadingMessages.create
    : (getRoleLoading || dataLoading)
      ? createRoleModal.loadingMessages.loading
      : createRoleModal.loadingMessages.update;

  // ── Sections ──────────────────────────────────────────────────────────────────
  const sections = {
    [SLUG.BASIC_INFORMATION]: {
      outerTitle: createRoleModal.sectionTitles.basicInformation.outer,
      innerTitle: createRoleModal.sectionTitles.basicInformation.outer,
      component: (
        <RoleBasicDetailsSection
          formData={formData}
          onChange={handleChange}
          onBlur={handleBlur}
          errors={errors}
          mode={mode}
        />
      ),
    },
    [SLUG.BRAND_ACCESS]: {
      outerTitle: createRoleModal.sectionTitles.brandAccess.outer,
      component: (
        <RoleBrandAccessSection
          selectedBrandId={formData.brandId}
          onChange={(newVal) => handleChange('brandId', newVal)}
          brands={brands}
          showAdminButton={showAdminButton}
          errors={errors}
        />
      ),
    },
    [SLUG.PERMISSIONS]: {
      outerTitle: createRoleModal.sectionTitles.permissions.outer,
      component: (
        <RolePermissionsSection
          selectedPermissionRoleIds={formData.roleIds}
          onChange={(newVal) => handleChange('roleIds', newVal)}
          mode={mode}
          roleGroupId={isEdit ? (getRoleData?.roleGroupId || editRoleRow?.roleGroupId) : null}
          initialPermissionRoleIds={getRoleData?.roleIds}
          permissionsHierarchy={permissionsHierarchy}
          flatPermissions={flatPermissions}
          permissionsLoading={dataLoading}
        />
      ),
    },
  };

  return (
    <>
      <ManagerModal
        open={open}
        onClose={handleClose}
        header={isEdit ? modalHeader.edit : modalHeader.create}
        onSave={handleSave}
        isLoading={isLoading}
        loadingMessage={loadingMessage}
        navConfig={navConfig}
        sections={sections}
        defaultSlug={defaultSlug}
        hashPrefix={hashPrefix}
      />
      <IndicatorModal
        open={showConfirmClose}
        title={unsavedChangesConfirm.title}
        message={unsavedChangesConfirm.message}
        variant="warning"
        onClose={() => setShowConfirmClose(false)}
        actions={[
          {
            label: unsavedChangesConfirm.discardButton,
            onClick: () => {
              setShowConfirmClose(false);
              onClose();
            },
            backgroundColor: '#ef4444',
            color: '#ffffff',
          },
        ]}
        actionsAlignment="center"
        maxWidth="420px"
      />
    </>
  );
}

export default CreateEditRoleModal;
